/**
 * Trusted-device enrolment. While the admin policy requires it, a sign-in on a new device gets
 * 403 DEVICE_APPROVAL_REQUIRED until a passkey or an already trusted device confirms it. The
 * request layer asks the registered handler (the approval dialog) and retries once on success.
 */
export type DeviceApprovalHandler = () => Promise<boolean>;

let handler: DeviceApprovalHandler | undefined;
let pending: Promise<boolean> | undefined;

/** Registers the dialog; returns the unregister function. Only one handler is active. */
export function setDeviceApprovalHandler(next: DeviceApprovalHandler): () => void {
  handler = next;
  return (): void => {
    if (handler === next) {
      handler = undefined;
    }
  };
}

/** True once the device is enrolled. Parallel requests share one prompt. */
export async function requestDeviceEnrolment(): Promise<boolean> {
  const current = handler;
  if (current === undefined) {
    return false;
  }
  if (pending === undefined) {
    pending = current().finally((): void => {
      pending = undefined;
    });
  }
  return pending;
}
